import React, { createContext, useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import LoadingModal from './LoadingModal';

// Ensure API_URL is properly defined with a fallback
const API_URL = import.meta.env.VITE_API_URL || 'https://react-app-production-3114.up.railway.app';

// Add cache helper functions at the top of the file
const CACHE_KEY = 'asin_fnsku_cache';
const CACHE_EXPIRY = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds

const getCache = () => {
  try {
    const cache = localStorage.getItem(CACHE_KEY);
    if (cache) {
      const { data, timestamp } = JSON.parse(cache);
      // Check if cache is expired (7 days)
      if (Date.now() - timestamp < CACHE_EXPIRY) {
        return data;
      }
      // Clear expired cache
      localStorage.removeItem(CACHE_KEY);
    }
  } catch (error) {
    console.error('Error reading cache:', error);
  }
  return {};
};

const updateCache = (sku, data) => {
  try {
    const existingCache = getCache();
    const updatedCache = {
      ...existingCache,
      [sku]: data
    };
    localStorage.setItem(CACHE_KEY, JSON.stringify({
      data: updatedCache,
      timestamp: Date.now()
    }));
  } catch (error) {
    console.error('Error updating cache:', error);
  }
};

export const BoxContext = createContext();

export const BoxProvider = ({ children }) => {
  const { shipmentId } = useParams(); // Use useParams to get the shipmentId from the URL

  const [boxes, setBoxes] = useState([]);
  const [shipmentInfo, setShipmentInfo] = useState(null);
  const [shipmentItems, setShipmentItems] = useState([]);
  const [asinData, setAsinData] = useState([]);
  const [servicesData, setServicesData] = useState([]);
  const [clientsData, setClientsData] = useState([]);
  const [clientInfo, setClientInfo] = useState(null);
  const [standardBoxes, setStandardBoxes] = useState([]);
  const [shelvingData, setShelvingData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null); // New state variable
  const [selectedNickname, setSelectedNickname] = useState(null);
  const [packingGroups, setPackingGroups] = useState([]);
  const [selectedPackingGroup, setSelectedPackingGroup] = useState(null);
  const [isMatchingIdentifiers, setIsMatchingIdentifiers] = useState(false);
  const [matchingMessage, setMatchingMessage] = useState('');
  const [itemsToProcess, setItemsToProcess] = useState(0);
  const [itemsProcessed, setItemsProcessed] = useState(0);
  const [isReloading, setIsReloading] = useState(false);
  // Add FDA classifications state
  const [fdaClassifications, setFdaClassifications] = useState({});

  // Function to synchronize box item weights with ASIN data
  const synchronizeBoxItemWeights = useCallback(() => {
    if (asinData.length > 0 && boxes.length > 0) {
      let madeChanges = false;
      
      const updatedBoxes = boxes.map(box => {
        // Ensure box.items is an array before mapping
        if (!Array.isArray(box.items)) {
          return box;
        }

        const updatedItems = box.items.map(item => {
          const matchingAsin = asinData.find(ad => ad.ASIN === item.ASIN);
          
          if (matchingAsin && typeof matchingAsin.WEIGHT !== 'undefined' && matchingAsin.WEIGHT !== null) {
            // IMPORTANT: Always store UNIT weight (per item), never multiply by quantity
            const itemWeight = parseFloat(item.WEIGHT);
            const asinWeight = parseFloat(matchingAsin.WEIGHT);

            // Check if both are valid numbers and if they differ
            if (!isNaN(asinWeight) && (isNaN(itemWeight) || itemWeight.toFixed(4) !== asinWeight.toFixed(4))) {
              madeChanges = true;
              return { ...item, WEIGHT: asinWeight }; // Store unit weight, not total weight
            }
          }
          return item;
        });
        
        return { ...box, items: updatedItems };
      });

      if (madeChanges) {
        setBoxes(updatedBoxes);
      }
    }
  }, [asinData, boxes]);

  // This effect will synchronize box item weights whenever asinData or boxes change
  useEffect(() => {
    synchronizeBoxItemWeights();
  }, [asinData, boxes]);

  // Add FDA classification fetching function
  const fetchFdaClassifications = useCallback(async (items, clientCountry) => {
    if (clientCountry !== 'USA' || !items || items.length === 0) {
      return {};
    }

    try {
      const products = items
        .filter(item => item?.item?.asin && item?.item?.title)
        .map(item => ({
          title: item.item.title,
          asin: item.item.asin
        }));

      if (products.length === 0) return {};

      const SERVER_API_URL = import.meta.env.VITE_SERVER_API_URL || 'http://localhost:5000';
      const response = await fetch(`${SERVER_API_URL}/api/classify-fda-products`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          products,
          includePriorNotice: true
        })
      });

      if (!response.ok) {
        throw new Error('Failed to fetch FDA classifications');
      }

      const data = await response.json();
      if (data.success && data.results) {
        console.log('Fetched FDA classifications for shipment:', Object.keys(data.results).length, 'items');
        return data.results;
      }
      return {};
    } catch (error) {
      console.error('Error fetching FDA classifications for shipment:', error);
      return {};
    }
  }, []);

  const fetchShipmentData = useCallback(async () => {
    setLoading(true);
    try {
      const [shipmentResponse, itemsResponse] = await Promise.all([
        fetch(`https://app.yyzprep.ca/api/shipments/outbound/${shipmentId}`, {
          headers: {
            'Authorization': 'Bearer Yh7l5CUTaZ1nIgAueWglafvm616hchHFFZxRjKjPHNBjB19b2jTDgGoCSpeq',
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          }
        }).then(response => response.json()),
        fetch(`https://app.yyzprep.ca/api/shipments/outbound/${shipmentId}/outbound-shipment-item`, {
          headers: {
            'Authorization': 'Bearer Yh7l5CUTaZ1nIgAueWglafvm616hchHFFZxRjKjPHNBjB19b2jTDgGoCSpeq',
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          }
        }).then(response => response.json())
      ]);

      setShipmentInfo(shipmentResponse.shipment);
      
      // Get cache
      const cache = getCache();
      
      // Check if any items have blank ASINs or FNSKUs
      const itemsWithBlankIdentifiers = itemsResponse.items.filter(item => {
        const merchantSku = item.item.merchant_sku;
        // Check if we have valid data in cache
        if (cache[merchantSku]) {
          // Only use cache data if it's better than what we already have
          if (cache[merchantSku].asin && !item.item.asin) {
            item.item.asin = cache[merchantSku].asin;
          }
          if (cache[merchantSku].fnsku && !item.item.fnsku) {
            item.item.fnsku = cache[merchantSku].fnsku;
          }
          
          // Check if we still need to process this item
          return !item.item.asin || !item.item.fnsku;
        }
        
        return !item.item.asin || !item.item.fnsku;
      });

      if (itemsWithBlankIdentifiers.length > 0) {
        setIsMatchingIdentifiers(true);
        setItemsToProcess(itemsWithBlankIdentifiers.length);
        setItemsProcessed(0);
        setMatchingMessage(`Matching ASINs and FNSKUs for ${itemsWithBlankIdentifiers.length} items...`);

        // Get the client's Amazon channel ID
        const teamId = shipmentResponse.shipment.team_id;
        console.log('Fetching Amazon channel for team ID:', teamId);
        const channelsResponse = await fetch(`https://app.yyzprep.ca/api/channels?api_token=Yh7l5CUTaZ1nIgAueWglafvm616hchHFFZxRjKjPHNBjB19b2jTDgGoCSpeq`, {
          headers: {
            'x-selected-client-id': teamId,
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          }
        });
        
        const channelsData = await channelsResponse.json();
        console.log('Channels data:', channelsData);
        const amazonChannel = channelsData.channels.find(channel => channel.type === 'amazon');
        
        if (amazonChannel) {
          const amazonChannelId = amazonChannel.id;
          
          // Process each item with blank identifiers
          for (const [index, item] of itemsWithBlankIdentifiers.entries()) {
            const merchantSku = item.item.merchant_sku;
            if (merchantSku) {
              setMatchingMessage(`Processing item ${index + 1} of ${itemsWithBlankIdentifiers.length}: ${merchantSku}`);
              
              const listingsUrl = `https://app.yyzprep.ca/api/channels/${amazonChannelId}/listings?page=1&per_page=20&q=sku%7E%22${merchantSku}%22+or+channel_identifier%7E%22${merchantSku}%22+or+title%7E%22${merchantSku}%22+or+identifiers.identifier%7E%22${merchantSku}%22&api_token=Yh7l5CUTaZ1nIgAueWglafvm616hchHFFZxRjKjPHNBjB19b2jTDgGoCSpeq`;
              
              const listingsResponse = await fetch(listingsUrl, {
                headers: {
                  'Accept': 'application/json',
                  'x-service-provider-id': '7',
                  'x-selected-client-id': teamId
                }
              });
              const listingsData = await listingsResponse.json();
              
              if (listingsData.data && listingsData.data.length > 0) {
                const listing = listingsData.data.find(listing => listing.sku === merchantSku);
                
                if (listing) {
                  const asinIdentifier = listing.identifiers.find(id => id.type === 'ASIN');
                  const fnskuIdentifier = listing.identifiers.find(id => id.type === 'FNSKU');
                  
                  const identifierData = {
                    asin: asinIdentifier?.identifier || null,
                    fnsku: fnskuIdentifier?.identifier || null
                  };
                  
                  // Update item and cache if we found either identifier
                  if (identifierData.asin || identifierData.fnsku) {
                    if (identifierData.asin) item.item.asin = identifierData.asin;
                    if (identifierData.fnsku) item.item.fnsku = identifierData.fnsku;
                    updateCache(merchantSku, identifierData);
                  }
                }
              }
              setItemsProcessed(index + 1);
            }
          }
        }
        setIsMatchingIdentifiers(false);
      }
      
      setShipmentItems(itemsResponse.items);
      
      await getPlans();

      // Get client data first to determine country before fetching prices
      const clientsDataResponse = await fetch(`${API_URL}/api/get_clients`)
        .then(response => response.json());

      const client = clientsDataResponse.find(client => client.id === shipmentResponse.shipment.team_id);
      const clientCountry = client && client.name.includes('(US)') ? 'USA' : 
                           client && client.name.includes('(CA)') ? 'Canada' : 'Unknown';
      
      if (client) {
        setClientInfo({ 
          name: client.name,
          id: client.id,
          country: clientCountry,
          email: client.email
        });
      }

      // Fetch FDA classifications for US shipments
      if (clientCountry === 'USA') {
        console.log('Fetching FDA classifications for US shipment...');
        const fdaResults = await fetchFdaClassifications(itemsResponse.items, clientCountry);
        setFdaClassifications(fdaResults);
      }

      const asinList = itemsResponse.items.map(item => item.item.asin).filter(asin => asin);

      // Always fetch ASIN data if there's an ASIN list.
      let asinDataResponse = [];
      if (asinList.length > 0) {
        // Only refresh prices for US shipments
        if (clientCountry === 'USA') {
          try {
            const priceRefreshResponse = await fetch(`${API_URL}/api/refresh-prices`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({ 
                asins: asinList,
                employee: 'Auto-Refresh' 
              })
            });
            const priceRefreshResult = await priceRefreshResponse.json();
          } catch (error) {
            console.error('Price refresh failed (non-critical):', error);
          }
        }

        // Fetch ASIN data (contains weight, etc.) for all shipments
        asinDataResponse = await fetch(`${API_URL}/api/get_asin_data`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ asins: asinList })
        }).then(response => response.json());
      } else {
        asinDataResponse = [];
      }

      const [servicesDataResponse] = await Promise.all([
        fetch('https://app.yyzprep.ca/api/services?type[]=outbound_shipment_item&api_token=Yh7l5CUTaZ1nIgAueWglafvm616hchHFFZxRjKjPHNBjB19b2jTDgGoCSpeq')
          .then(response => response.json()),
        'default'
      ]);

      setAsinData(asinDataResponse);
      setServicesData(servicesDataResponse);
      setClientsData(clientsDataResponse);

      const skuList = itemsResponse.items.map(item => item.item.merchant_sku);
      const shelvingDataResponse = await fetch(`${API_URL}/api/get_shelving_data`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ skus: skuList }),
      }).then(response => response.json());

      setShelvingData(shelvingDataResponse);

      const boxData = await fetchBoxes();
      setBoxes(boxData);

      await fetchStandardBoxes();

      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setIsMatchingIdentifiers(false);
      setLoading(false);
    }
  }, [shipmentId]);



  const refreshShelving = async () => {
    const shelvingDataResponse = await fetch(`${API_URL}/api/get_shelving_data`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ skus: shipmentItems.map(item => item.item.merchant_sku) }),
    }).then(response => response.json());

    setShelvingData(shelvingDataResponse);
  };

  const getPlans = async () => {
    try {
      const response = await fetch(`https://app.yyzprep.ca/api/fba-transport/v2024/plans?api_token=Yh7l5CUTaZ1nIgAueWglafvm616hchHFFZxRjKjPHNBjB19b2jTDgGoCSpeq&outbound_shipment_id=${shipmentId}`);
      if (!response.ok) {
        throw new Error('Failed to fetch plans');
      }
      const data = await response.json();
      setPlans(data.data);
    } catch (error) {
      console.error('Error fetching plans:', error);
    }
  };



  
  const reloadShipmentData = useCallback(async () => {
    try {
      const [shipmentResponse, itemsResponse] = await Promise.all([
        fetch(`https://app.yyzprep.ca/api/shipments/outbound/${shipmentId}`, {
          headers: {
            'Authorization': 'Bearer Yh7l5CUTaZ1nIgAueWglafvm616hchHFFZxRjKjPHNBjB19b2jTDgGoCSpeq',
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          }
        }).then(response => response.json()),
        fetch(`https://app.yyzprep.ca/api/shipments/outbound/${shipmentId}/outbound-shipment-item`, {
          headers: {
            'Authorization': 'Bearer Yh7l5CUTaZ1nIgAueWglafvm616hchHFFZxRjKjPHNBjB19b2jTDgGoCSpeq',
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          }
        }).then(response => response.json())
      ]);
  
      // Check if any items have blank ASINs
      const itemsWithBlankAsins = itemsResponse.items.filter(item => !item.item.asin || !item.item.fnsku);
      if (itemsWithBlankAsins.length > 0) {
        // Get the client's Amazon channel ID
        const teamId = shipmentResponse.shipment.team_id;
        const channelsResponse = await fetch(`https://app.yyzprep.ca/api/channels?api_token=Yh7l5CUTaZ1nIgAueWglafvm616hchHFFZxRjKjPHNBjB19b2jTDgGoCSpeq`, {
          headers: {
            'x-selected-client-id': teamId,
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          }
        });
        
        const channelsData = await channelsResponse.json();
        const amazonChannel = channelsData.channels.find(channel => channel.type === 'amazon');
        
        if (amazonChannel) {
          const amazonChannelId = amazonChannel.id;
          
          // Process each item with blank ASIN or FNSKU
          for (const item of itemsWithBlankAsins) {
            const merchantSku = item.item.merchant_sku;
            if (merchantSku) {
              const listingsUrl = `https://app.yyzprep.ca/api/channels/${amazonChannelId}/listings?page=1&per_page=20&q=sku%7E%22${merchantSku}%22+or+channel_identifier%7E%22${merchantSku}%22+or+title%7E%22${merchantSku}%22+or+identifiers.identifier%7E%22${merchantSku}%22&api_token=Yh7l5CUTaZ1nIgAueWglafvm616hchHFFZxRjKjPHNBjB19b2jTDgGoCSpeq`;
              
              const listingsResponse = await fetch(listingsUrl);
              const listingsData = await listingsResponse.json();
              
              if (listingsData.data && listingsData.data.length > 0) {
                const listing = listingsData.data.find(listing => listing.sku === merchantSku);
                if (listing) {
                  const asinIdentifier = listing.identifiers.find(id => id.type === 'ASIN');
                  const fnskuIdentifier = listing.identifiers.find(id => id.type === 'FNSKU');
                  
                  if (asinIdentifier) {
                    item.item.asin = asinIdentifier.identifier;
                  }
                  
                  if (fnskuIdentifier) {
                    item.item.fnsku = fnskuIdentifier.identifier;
                  }
                }
              }
            }
          }
        }
      }

      setShipmentInfo(shipmentResponse.shipment);
      setShipmentItems(itemsResponse.items);
  
      await getPlans();
    } catch (error) {
      console.error('Error reloading shipment data:', error);
    }
  }, [shipmentId]);
  

  const fetchStandardBoxes = async () => {
    try {
      const response = await fetch(`${API_URL}/api/get_standard_boxes`);
      if (!response.ok) {
        throw new Error('Failed to fetch standard boxes');
      }
      const data = await response.json();
      setStandardBoxes(data);
    } catch (error) {
      console.error('Error fetching standard boxes:', error);
    }
  };

  const reloadAsinData = async () => {
    try {
      const asinList = shipmentItems.map(item => item.item.asin).filter(asin => asin);
      
      if (asinList.length === 0) {
        return;
      }
  
      const response = await fetch(`${API_URL}/api/get_asin_data`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ asins: asinList })
      });
  
      if (!response.ok) {
        throw new Error('Failed to fetch ASIN data');
      }
  
      const data = await response.json();
      setAsinData(data);
    } catch (error) {
      console.error('Error fetching ASIN data:', error);
    }
  };
  


  useEffect(() => {
    if (selectedPlan?.packing_options) {
      // Filter packing options with confirmed_at
      const confirmedOptions = selectedPlan.packing_options.filter(po => po.confirmed_at);
      
      if (confirmedOptions.length > 0) {
        // Find the most recent packing option
        const latestOption = confirmedOptions.reduce((latest, current) => 
          new Date(current.confirmed_at) > new Date(latest.confirmed_at) ? current : latest
        );
        
        setPackingGroups(latestOption.packing_groups);
      } else {
        setPackingGroups([]);
      }
    } else {
      setPackingGroups([]);
    }
  }, [selectedPlan]);
  

  const fetchBoxes = useCallback(async () => {
    const endpoint = `${API_URL}/api/get_box_contents_by_outbound/${shipmentId}`;
    
    try {
      const response = await fetch(endpoint);
      const data = await response.json();
      
      // Handle new response format with metadata
      let boxesData;
      if (data.boxes && Array.isArray(data.boxes)) {
        // New format with metadata
        boxesData = data.boxes;
      } else if (Array.isArray(data)) {
        // Legacy format (backwards compatibility)
        boxesData = data;
      } else {
        console.error('[BoxContext] Unexpected response format:', data);
        return [];
      }
      
      const groupedData = groupAndSortByBoxNumber(boxesData);
      setBoxes(groupedData);
      return groupedData;

    } catch (error) {
      console.error('[BoxContext] Error fetching boxes:', error);
      return [];
    }
  }, [shipmentId]);

  useEffect(() => {
    fetchShipmentData();
  }, [fetchShipmentData]);


  const updateShelvingQty = async (key, shelfCode, newQty, SKU ) => {
    const id_to_use = selectedPlan ? selectedPlan.id : shipmentId; // Use selectedPlan's ID if available, otherwise use "a"

    try {
      const response = await fetch(`${API_URL}/api/update_shelving_qty`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          key,
          qty: newQty,
          action_type: "BOX_CONTENTS_ADJUSTMENT",
          sku: SKU,
          shelfCode: shelfCode,
          shipmentId: id_to_use, // Use the conditionally set shipmentId
          employee: selectedNickname
        }),
      });


      if (response.ok) {
        refreshShelving();
        console.log('Shelving quantity updated successfully');
      } else {
        console.error('Failed to update shelving quantity');
        alert('Failed to update shelving quantity');
      }
    } catch (error) {
      console.error('Error updating shelving quantity:', error);
      alert('Error updating shelving quantity');
    }
  };

  const removeShelving = async (key,shelfCode, SKU ) => {
    const id_to_use = selectedPlan ? selectedPlan.id : shipmentId; // Use selectedPlan's ID if available, otherwise use "a"

    try {
      const response = await fetch(`${API_URL}/api/update_shelving_qty`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          key,
          sku: SKU,
          qty: 0,
          shelfCode: shelfCode,
          action_type: "BOX_CONTENTS_REMOVEFROMSHELF",
          shipmentId: id_to_use, // Use the conditionally set shipmentId
          employee: selectedNickname
        }),
      });

      if (response.ok) {
        refreshShelving();
        console.log('Shelving removed successfully');
      } else {
        console.error('Failed to remove shelving');
        alert('Failed to remove shelving');
      }
    } catch (error) {
      console.error('Error removing shelving:', error);
      alert('Error removing shelving');
    }
  };


  return (
    <BoxContext.Provider value={{ 
      boxes, 
      shipmentInfo, 
      shipmentItems, 
      asinData, 
      servicesData, 
      refreshStandardBoxes: fetchStandardBoxes,
      standardBoxes,
      reloadAsinData,
      setBoxes,
      clientsData, 
      clientInfo, 
      shelvingData,
      plans,
      reloadShipmentData,
      refreshShelving,
      updateShelvingQty,
      removeShelving,
      selectedPlan,
      selectedNickname,
      setSelectedNickname,
      setSelectedPlan,
      loading,
      refreshBoxes: fetchBoxes,
      setSelectedPackingGroup,
      packingGroups,
      selectedPackingGroup,
      fdaClassifications,
      shipmentId
    }}>
      <LoadingModal 
        open={isMatchingIdentifiers} 
        message={`${matchingMessage}${itemsToProcess > 0 ? ` (${itemsProcessed}/${itemsToProcess})` : ''}`}
      />
      {children}
    </BoxContext.Provider>
  );
};

const groupAndSortByBoxNumber = (data) => {
  const groupedData = data.reduce((acc, item) => {
    const boxNumber = item.BOX_NUMBER;
    if (!acc[boxNumber]) {
      acc[boxNumber] = {
        length: item.LENGTH,
        width: item.WIDTH,
        height: item.HEIGHT,
        weight: item.WEIGHT,
        PALLET: item.PALLET,
        items: []
      };
    }
    
    // Only push non-empty items into the items array
    if (item.FNSKU || item.SKU || item.ASIN || item.QTY || item.TITLE) {
      acc[boxNumber].items.push(item);
    }

    return acc;
  }, {});

  const sortedData = Object.keys(groupedData)
    .sort((a, b) => Number(a) - Number(b))
    .map(boxNumber => ({
      boxNumber,
      length: groupedData[boxNumber].length,
      width: groupedData[boxNumber].width,
      height: groupedData[boxNumber].height,
      weight: groupedData[boxNumber].weight,
      PALLET: groupedData[boxNumber].PALLET,
      items: groupedData[boxNumber].items
    }));

  return sortedData;
};
